import React, { PropsWithChildren, useCallback, useState } from "react"
import styled from "styled-components"
import { State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { hasErrorsAll } from "../../globalStates/utils"
import ErrorBoundary from "../ErrorBoundary"
import { QrCodeTool, RemoveTool } from "./SectionTools"
import OverlayTrigger from "react-bootstrap/esm/OverlayTrigger"
import Tooltip from "react-bootstrap/esm/Tooltip"
import ArrowIcon from "../../images/arrow.svg"
import useStaticTexts from "../../utils/StaticTexts"
import { DemoProductStatus } from "../../globalStates/ExhibitorDataTypes"

const Wrapper = styled.div`
    border: 1px solid #d9d9d9;
    box-sizing: border-box;
    border-radius: 5px;
    margin-bottom: 30px;
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 25px;
    background-color: #d9d9d9;
    font-weight: bold;
    cursor: pointer;
`

const Title = styled.div`
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 20px;
    max-height: calc(20px * 3);
    gap: 15px;
    color: #444650;

    &.invalid {
        color: #dc3545;
    }
`

const ActionBar = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;

    & button {
        width: 100px;
        margin: -5px 5px 0 0;
        font-size: 12px;
        line-height: 14px;
        text-align: center;
    }
`

const StatusDot = styled.span<{ color: string }>`
    height: 10px;
    width: 10px;
    border-radius: 50%;
    background-color: ${(props) => props.color};
    display: inline-block;
    margin-left: 10px;
`

interface ContentProps {
    visible: boolean
}

//TODO add animation/transition
const Content = styled.div<ContentProps>`
    display: ${(props) => (props.visible ? "block" : "none")};
    // height:  ${(props) => (props.visible ? "auto" : "0px")};
    // transition: height 15s ease-out;
    padding: 20px 25px 5px 25px;
`

interface SectionProps {
    id: string
    title: string
    sideTitle?: string
    includeQrCode?: boolean
    customEndComponent?: React.JSX.Element
    collapsible?: boolean
    closed?: boolean
    opacity?: number
    onClickTitle?: () => void
    onClickQrCode?: () => void
    onClickRemove?: () => void
    entityState?: State<any, Validation>
    status?: DemoProductStatus
}

const Section = React.forwardRef<HTMLDivElement, PropsWithChildren<SectionProps>>(
    (
        {
            id,
            title,
            sideTitle,
            includeQrCode,
            customEndComponent,
            collapsible = false,
            closed = false,
            opacity = 1,
            onClickTitle,
            onClickQrCode,
            onClickRemove,
            entityState,
            status,
            children
        },
        ref
    ) => {
        const staticTexts = useStaticTexts()

        const isInvalid = hasErrorsAll(entityState)
        const [open, setOpen] = useState(!collapsible)

        function getStatusColor() {
            switch (status) {
                case "Available":
                    return "#008000"
                case "CheckedOut":
                    return "#FFA500"
                case "OutOfOrder":
                    return "#FF0000"
                default:
                    return "transparent"
            }
        }

        function getStatusText() {
            switch (status) {
                case "Available":
                    return staticTexts.demoProductStatus.available
                case "CheckedOut":
                    return staticTexts.demoProductStatus.used
                case "OutOfOrder":
                    return staticTexts.demoProductStatus.outOfOrder
                default:
                    return ""
            }
        }

        const onClickHandler = useCallback(
            (event: React.MouseEvent<HTMLDivElement>) => {
                event.preventDefault()
                event.stopPropagation()
                if (closed) return

                if (collapsible) setOpen(!open)
                onClickTitle && onClickTitle()
            },
            [collapsible, closed, onClickTitle, open]
        )

        return (
            <Wrapper style={{ opacity }}>
                <Header ref={ref} onClick={onClickHandler}>
                    <Title className={isInvalid ? "invalid" : ""}>
                        {collapsible && (
                            <img
                                src={ArrowIcon}
                                alt="icon"
                                style={{
                                    marginBottom: "3px",
                                    marginRight: "10px",
                                    transform: open ? undefined : "rotate(180deg)"
                                }}
                            />
                        )}
                        {title}{" "}
                        {status && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="delete" style={{ fontSize: "12px" }}>
                                        {`${staticTexts.status}: ${getStatusText()}`}
                                    </Tooltip>
                                }
                            >
                                <StatusDot color={getStatusColor()} />
                            </OverlayTrigger>
                        )}
                    </Title>
                    <ActionBar>
                        {customEndComponent}
                        {sideTitle && <Title>{sideTitle}</Title>}
                        {includeQrCode === true && onClickQrCode && !(Number(id) <= 0) && <QrCodeTool onClick={onClickQrCode} />}
                        {onClickRemove && <RemoveTool onClick={onClickRemove} />}
                    </ActionBar>
                </Header>
                {open && (
                    <ErrorBoundary>
                        <Content visible={open}>{children}</Content>
                    </ErrorBoundary>
                )}
            </Wrapper>
        )
    }
)

export default Section
